import { Injectable } from '@angular/core';

/**
 * Servicio para bloquear el cierre de drawers con la tecla Escape.
 *
 * Este servicio previene que los drawers se cierren accidentalmente
 * cuando el usuario presiona la tecla Escape, mejorando la UX y
 * evitando pérdida de datos no guardados.
 *
 * Uso en componentes de drawer:
 *
 * ```typescript
 * export class MyDrawerComponent implements OnInit, OnDestroy {
 *   constructor(private drawerEscapeBlocker: DrawerEscapeBlockerService) {}
 *
 *   ngOnInit(): void {
 *     this.drawerEscapeBlocker.block();
 *   }
 *
 *   ngOnDestroy(): void {
 *     this.drawerEscapeBlocker.unblock();
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DrawerEscapeBlockerService {
  private escapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private isBlocked = false;

  constructor() {}

  /**
   * Bloquea la tecla Escape para prevenir el cierre de drawers.
   * Este método registra un event listener global que intercepta
   * el evento antes de que llegue a los componentes de Ng-Zorro.
   */
  block(): void {
    // Si ya está bloqueado, no hacer nada
    if (this.isBlocked) {
      return;
    }

    // Crear el handler que intercepta la tecla Escape
    this.escapeHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();

        // Detener la propagación inmediata si está disponible
        if (typeof (event as any).stopImmediatePropagation === 'function') {
          (event as any).stopImmediatePropagation();
        } else {
          event.stopPropagation();
        }
      }
    };

    // Registrar el listener en la fase de captura (true) para interceptar
    // el evento antes de que llegue a otros componentes
    window.addEventListener('keydown', this.escapeHandler, true);
    this.isBlocked = true;
  }

  /**
   * Desbloquea la tecla Escape, removiendo el event listener.
   * Este método debe ser llamado en ngOnDestroy del componente
   * para evitar memory leaks.
   */
  unblock(): void {
    if (this.escapeHandler && this.isBlocked) {
      window.removeEventListener('keydown', this.escapeHandler, true);
      this.escapeHandler = null;
      this.isBlocked = false;
    }
  }

  /**
   * Retorna el estado actual del bloqueador.
   * @returns true si la tecla Escape está bloqueada, false en caso contrario
   */
  isEscapeBlocked(): boolean {
    return this.isBlocked;
  }
}
